import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface TemplateDiff {
  template_id: number;
  current_version: number;
  used_version: number;
  has_changes: boolean;
  added: Array<{
    template_position_id: number;
    position_number: number;
    title: string;
    is_group_header: boolean;
    suggested_sort_order: number;
  }>;
  renamed: Array<{
    template_position_id: number;
    task_id: number;
    section_id: number;
    section_name: string;
    old_title: string;
    new_title: string;
  }>;
  removed: Array<{
    task_id: number;
    section_id: number;
    section_name: string;
    title: string;
    has_data: boolean;
  }>;
  sections: Array<{ id: number; name: string }>;
}

export interface ApplyDiffInput {
  add: Array<{ template_position_id: number; section_id: number }>;
  rename: number[];
  remove: number[];
}

export function useTemplateDiff(projectId: number | undefined) {
  return useQuery({
    queryKey: ['template-diff', projectId],
    queryFn: async () => {
      const { data } = await api.get<TemplateDiff | null>(`/projects/${projectId}/template-diff`);
      return data;
    },
    enabled: projectId !== undefined,
    staleTime: 60_000,
  });
}

export function useApplyTemplateDiff(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ApplyDiffInput) => {
      const { data } = await api.post<{ applied: { added: number; renamed: number; removed: number } }>(
        `/projects/${projectId}/template-apply`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['template-diff', projectId] });
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      qc.invalidateQueries({ queryKey: ['sections', projectId] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['gantt'] });
    },
  });
}
